import { useQuery } from '@tanstack/react-query';
import { apiClient as api } from '@/lib/api';

export interface FleetLiveItem {
  vehicleId: string;
  code: string;
  plateEn: string | null;
  plateAr: string | null;
  vehicleType: string;
  status: string;
  investorId: string;
  investorName: string | null;
  legalEntityName: string | null;
  currentSiteId: string | null;
  currentSiteName: string | null;
  isTracked: boolean;
  afaqyUnitId: string | null;
  afaqyUnitName: string | null;
  lastLat: number | null;
  lastLng: number | null;
  lastSpeedKmh: number | null;
  lastUpdateAt: string | null;
  googleMapsUrl: string | null;
  yesterdayKmDriven: number | null;
  yesterdayMaxSpeedKmh: number | null;
}

export interface FleetLiveResponse {
  items: FleetLiveItem[];
  total: number;
  tracked: number;
  serverTime: string;
}

/**
 * Fleet live tracking. Auto-refetches every 30 seconds while the page is open.
 * Backend data freshness is bounded by the worker sync (every 15 min by default),
 * so refetching faster than that gains nothing — we still poll every 30s in case
 * the user has the page open during a sync, so they see new data immediately.
 */
export function useFleetLive() {
  return useQuery<FleetLiveResponse>({
    queryKey: ['fleet-live'],
    queryFn: async () => {
      const r = await api.get('/api/v1/afaqy/fleet/live');
      return r.data;
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });
}

/**
 * Manually re-run plate-based auto-mapping. Admin-only.
 * Usage:
 *   const m = useAutoMap();
 *   m.mutate(undefined, { onSuccess: (r) => alert(`Mapped ${r.newlyMapped}`) });
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

export function useAutoMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post('/api/v1/afaqy/fleet/auto-map');
      return r.data as { ok: boolean; newlyMapped: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fleet-live'] });
    },
  });
}
